import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, UserTier, UserStatus, ROUTE_PATHS } from '@/lib/index';
import { supabase } from '@/integrations/supabase/client';

interface AuthUser extends User {
  sessionToken?: string;
}

/**
 * Simplified Secure Authentication Hook
 * Uses direct database queries with service role for better reliability
 */
export const useAuth = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  // Always-fresh ref so refreshUser never captures a stale user object
  const userRef = useRef<AuthUser | null>(null);
  // Keep userRef always in sync with user state
  useEffect(() => { userRef.current = user; }, [user]);

  // Session timeout: 30 minutes (in milliseconds)
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

  // Check for existing session on mount and validate timeout
  useEffect(() => {
    const checkSession = async () => {
      console.log('useAuth: Checking session on mount');
      const userData = localStorage.getItem('userData');
      const lastActivity = localStorage.getItem('lastActivity');
      console.log('useAuth: userData from localStorage:', userData);
      console.log('useAuth: lastActivity from localStorage:', lastActivity);
      
      if (userData && lastActivity) {
        try {
          const parsedUser = JSON.parse(userData);
          const lastActivityTime = parseInt(lastActivity);
          const now = Date.now();
          const timeSinceLastActivity = now - lastActivityTime;
          
          console.log('useAuth: Time since last activity:', timeSinceLastActivity, 'ms');
          
          // Check if session has expired (30 minutes of inactivity)
          if (timeSinceLastActivity > SESSION_TIMEOUT) {
            console.log('useAuth: Session expired due to inactivity');
            localStorage.removeItem('userData');
            localStorage.removeItem('lastActivity');
            setUser(null);
          } else {
            console.log('useAuth: Session valid, restoring user');
            setUser(parsedUser);
            // Update last activity time
            localStorage.setItem('lastActivity', now.toString());
          }
        } catch (error) {
          console.error('useAuth: Session check failed:', error);
          localStorage.removeItem('userData');
          localStorage.removeItem('lastActivity');
        }
      } else {
        console.log('useAuth: No valid session found');
      }
      
      console.log('useAuth: Setting isLoading to false');
      setIsLoading(false);
    };

    checkSession();
  }, []);

  // Track user activity and update last activity timestamp
  useEffect(() => {
    if (!user) return;

    const updateActivity = () => {
      const now = Date.now();
      localStorage.setItem('lastActivity', now.toString());
      console.log('useAuth: Activity updated at', new Date(now).toLocaleTimeString());
    };

    // Update activity on user interactions
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    // Check session validity every minute
    const sessionCheckInterval = setInterval(() => {
      const lastActivity = localStorage.getItem('lastActivity');
      if (lastActivity) {
        const lastActivityTime = parseInt(lastActivity);
        const now = Date.now();
        const timeSinceLastActivity = now - lastActivityTime;
        
        if (timeSinceLastActivity > SESSION_TIMEOUT) {
          console.log('useAuth: Session expired, logging out');
          localStorage.removeItem('userData');
          localStorage.removeItem('lastActivity');
          setUser(null);
          navigate(ROUTE_PATHS.LOGIN);
        }
      }
    }, 60000); // Check every minute

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(sessionCheckInterval);
    };
  }, [user, navigate]);

const login = useCallback(async (username: string, password: string) => {
    try {
      setIsLoading(true);
      console.log('useAuth: Starting login process for:', username);
      
      // Call simple authentication Edge Function
      console.log('useAuth: Calling Simple Edge Function...');
      const { data, error } = await supabase.functions.invoke('auth_with_bcrypt_2026_03_26_08_35', {
        body: { username, password }
      });

      console.log('useAuth: Edge Function response:', { data, error });

      // supabase.functions.invoke puts non-2xx responses into `error`, not `data`.
      // The actual JSON body (with our error message) lives in error.context.
      if (error) {
        console.error('useAuth: Edge function error:', error);
        // Try to extract the real error message from the response body
        let errMsg = 'Authentication failed';
        try {
          const ctx = (error as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.error) errMsg = body.error;
          }
        } catch (_) { /* fall through to generic message */ }
        throw new Error(errMsg);
      }

      if (!data || !data.success) {
        console.error('useAuth: Login failed:', data);
        throw new Error(data?.error || 'Login failed');
      }

const { user: userData, sessionToken } = data;
      const authUser = { ...userData, sessionToken };

      console.log('useAuth: Login successful, user data:', authUser);

      // Store session securely with activity timestamp
      localStorage.setItem('userData', JSON.stringify(authUser));
      localStorage.setItem('lastActivity', Date.now().toString());
      console.log('useAuth: Session created with activity timestamp');

      // Navigate immediately — login log fires in background (no await)
      setUser(authUser);
      console.log('useAuth: Navigating to match betting...');
      navigate(ROUTE_PATHS.MATCHES);

      // Fire-and-forget: record login event without blocking navigation
      (async () => {
        try {
          const ipRes = await fetch('https://api.ipify.org?format=json').catch((): null => null);
          const ipData = ipRes ? await ipRes.json().catch((): null => null) : null;
          const ipAddress: string | null = ipData?.ip || null;
          const { data: logData } = await supabase.functions.invoke('user_logs_2026_05_13', {
            body: { action: 'login', userId: authUser.id, username: authUser.username, ipAddress }
          });
          if (logData?.logId) {
            localStorage.setItem('userLogId', logData.logId);
          }
        } catch (_) { /* non-critical */ }
      })();

      return { success: true };
    } catch (error) {
      console.error('useAuth: Login error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Login failed' 
      };
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

const logout = useCallback(async () => {
    try {
      // Record logout event
      const logId = localStorage.getItem('userLogId');
      if (logId) {
        await supabase.functions.invoke('user_logs_2026_05_13', {
          body: { action: 'logout', logId }
        }).catch(() => {});
        localStorage.removeItem('userLogId');
      }
      // Clear local storage including activity timestamp
      localStorage.removeItem('userData');
      localStorage.removeItem('lastActivity');
      console.log('useAuth: Session cleared, logging out');
      setUser(null);
      navigate(ROUTE_PATHS.LOGIN);
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [navigate]);

  // Helper functions for role checking
  const isAuthenticated = !!user;
  const isMaster = () => user?.tier === UserTier.MASTER;
  // Master has all company-level privileges
  const isCompany = () => user?.tier === UserTier.COMPANY || user?.tier === UserTier.MASTER;
  const isAgent = () => user?.tier === UserTier.AGENT;
  const isPlayer = () => user?.tier === UserTier.PLAYER;
  
  const isSuspended = user?.status === UserStatus.SUSPENDED;
  const isBanned = user?.status === UserStatus.BANNED;
  const effectiveStatus = isBanned ? UserStatus.BANNED : isSuspended ? UserStatus.SUSPENDED : UserStatus.ACTIVE;

  // Permission checks
  const canPlaceBets = useCallback(() => {
    if (!isAuthenticated || isBanned || user?.status !== UserStatus.ACTIVE) return false;
    // Only PLAYER tier can place bets
    if (user?.tier !== UserTier.PLAYER) return false;
    // Block betting if no remaining credit
    const available = user?.availableCreditLimit ?? user?.creditLimit ?? 0;
    if (available <= 0) return false;
    return true;
  }, [isAuthenticated, isBanned, user?.status, user?.tier, user?.availableCreditLimit, user?.creditLimit]);

  const canManageUser = useCallback((targetUser: User) => {
    if (!user || isBanned || isSuspended) return false;
    
    if (isCompany()) return true;
    if (isAgent()) {
      return targetUser.parentId === user.id || targetUser.id === user.id;
    }
    return targetUser.id === user.id; // Players can only manage themselves
  }, [user, isBanned, isSuspended, isCompany, isAgent]);

  const canSetFightPercentage = useCallback((targetUser: User) => {
    if (!user || !isCompany()) return false;
    return targetUser.tier === UserTier.AGENT;
  }, [user, isCompany]);

  // Refresh user data from database
  // Uses userRef (always-fresh) so this callback never becomes stale
  const refreshUser = useCallback(async () => {
    const currentUser = userRef.current;
    if (!currentUser?.id) return;

    try {
      console.log('[useAuth] Refreshing user data from database...');

      const { data, error } = await supabase
        .from('users')
        .select('id, username, nickname, tier, status, balance, credit_limit, available_credit_limit, ticket_price, fight_percentage, fight_pct_limit, fight_on_off, first_login, parent_id')
        .eq('id', currentUser.id)
        .single();

      if (error) {
        console.error('[useAuth] Error refreshing user:', error);
        return;
      }

      if (data) {
        const updatedUser: AuthUser = {
          id: data.id,
          username: data.username,
          nickname: data.nickname || undefined,
          tier: data.tier as UserTier,
          status: data.status as UserStatus,
          balance: parseFloat(data.balance || 0),
          creditLimit: parseFloat(data.credit_limit || 0),
          availableCreditLimit: data.available_credit_limit !== null && data.available_credit_limit !== undefined
            ? parseFloat(data.available_credit_limit)
            : parseFloat(data.credit_limit || 0),
          ticketPrice: parseFloat(data.ticket_price || 0),
          fightPercentage: parseFloat(data.fight_percentage || 0),
          fightPctLimit: (() => {
            if (data.fight_pct_limit !== null && data.fight_pct_limit !== undefined)
              return parseInt(data.fight_pct_limit);
            // tier-aware fallback
            const t = data.tier as UserTier;
            const fp = parseFloat(data.fight_percentage || 0);
            if (t === UserTier.PLAYER) return 0;
            if (t === UserTier.AGENT) return fp;
            return 100;
          })(),
          fightOnOff: data.fight_on_off !== null && data.fight_on_off !== undefined ? Boolean(data.fight_on_off) : true,
          firstLogin: data.first_login !== false,
          parentId: data.parent_id,
          createdAt: currentUser.createdAt,
          sessionToken: currentUser.sessionToken
        };

        console.log('[useAuth] User data refreshed:', updatedUser);
        setUser(updatedUser);
        localStorage.setItem('userData', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('[useAuth] Failed to refresh user:', error);
    }
  }, []); // no deps — always uses userRef.current which is always fresh

  // Instantly patch the in-memory user state (and localStorage) without a DB round-trip.
  // Use this for immediate UI updates after a known-good write to the DB.
  const patchUser = useCallback((patch: Partial<User>) => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...patch };
      localStorage.setItem('userData', JSON.stringify(updated));
      return updated;
    });
  }, []);

  return {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    refreshUser,
    patchUser,
    isMaster,
    isCompany,
    isAgent,
    isPlayer,
    isSuspended,
    isBanned,
    effectiveStatus,
    canPlaceBets,
    canManageUser,
    canSetFightPercentage,
  };
};